import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import MultiSelectAutoCompleteViewModelType from '@/ViewModels/Controls/MultiSelectAutoCompleteViewModel';
import KendoDropDownHelper from '@/Utility/KendoDropDownHelper';
import Utilities from '@/Utility/Utilities';
import LocatorHelper from '@/Utility/LocatorHelper';

export default class MultiSelectAutoComplete extends Control {

    private jQueryControl: JQuery;

    /**
     * @override
     */
    public widget: KnockoutObservable<kendo.ui.MultiSelectExt>;

    public constructor(private viewModel: MultiSelectAutoCompleteViewModelType, private element?: JQuery) {
        super(viewModel, element);

        this.onWidgetCreated(this.onWidgetCreatedCallback.bind(this));
    }

    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.element || $('<div><select></div>');

        this.applyBindings(this.jQueryControl.find('select'), {
            kendoMultiSelect: {
                data: this.viewModel.dataSource,
                value: this.viewModel.values,
                ignoreCase: false,
                change: this.viewModel.onChange,
                select: this.viewModel.onSelect,
                minLength: this.viewModel.minLength,
                enabled: this.viewModel.isEnabled,
                filter: this.definePureComputed(() => {
                    if (this.viewModel.containsMode === true) {
                        return 'contains';
                    }

                    return 'startswith';
                }),
                dataBound: this.viewModel.dataBound,
                maxSelectedItems: this.viewModel.maxSelectedItems,
                dataTextField: this.viewModel.dataTextField,
                dataValueField: this.viewModel.dataValueField,
                template: this.viewModel.template,
                valueTemplate: this.viewModel.valueTemplate,
                widget: this.widget,
                close: () => {
                    // Beim schließen wird der Filter der DataSource wieder geleert. (Bug 21063)
                    if (this.viewModel && this.viewModel.dataSource && this.viewModel.dataSource.filter) {
                        let filters = this.viewModel.dataSource.filter();
                        if (filters) {
                            //clear applied filters
                            this.viewModel.dataSource.filter({});
                        }
                    }
                },
                open: () => {
                    if (this.viewModel.isEnabled() && Utilities.isFunction(this.viewModel.onOpen))
                        this.viewModel.onOpen();
                }
            },
            focus: this.viewModel.isFocusable
        });

        /**
         * Es werden bei einem STRG-A Event alle Objekte in der "listView" des kendoMultiSelect selektiert,
         * egal welchen Wert "maxSelectedItems" im kendoMultiSelect hat. Für eine (Association)SingleAutocompleteBox
         * (welche ein MultiSelectAutocomplete verwendet) soll aber maximal ein Objekt selektiert werden.
         * Ein Überschreiben des Event-Handlers in Kendo kommt nicht in Frage, weshalb wir im Nachhinein
         * manuell nur das erste Objekt selektieren.
         *
         * In der aktuellen Kendo-Version (Stand 04.03.2020) scheint "maxSelectedItems" jedoch berücksichtigt
         * zu werden, da dort nur Elemente entsprechend dieses Wertes bei STRG-A selektiert werden.
         */
        if (this.viewModel.maxSelectedItems() === 1) {
            this.jQueryControl.find('input').on('keydown.kendoListView', e => {
                if (e.ctrlKey && e.keyCode === 65) { // 'ctrl+a'
                    let widget = this.widget();
                    let values = widget.value();
                    if (values.length > 1) {
                        var firstValue = values[0];
                        widget.value(firstValue);
                        this.viewModel.onConnect(firstValue);
                        widget.close();
                    }
                }
            });
        }

        return this.jQueryControl;
    }

    private onWidgetCreatedCallback(widget: kendo.ui.MultiSelectExt): void {
        this.applyBindings(this.jQueryControl, {
            controlName: 'multiselectautocomplete',
            loader: this.viewModel.isBusy,
            controlIdent: this.identifier,
            tooltip: this.viewModel.tooltip,
            invalid: this.viewModel.invalidationText,
            visible: this.viewModel.isVisible,
            styles: this.definePureComputed({
                read: () => {
                    let styles = ['default'];
                    return styles;
                }
            }),
            states: this.definePureComputed({
                read: () => {
                    let states = [];
                    if (this.viewModel.isRequired() === true) states.push('required');
                    if (this.viewModel.isValid() === true) states.push('valid');
                    if (this.viewModel.isValid() !== true) states.push('invalid');
                    if (this.viewModel.isEnabled() === true) states.push('enabled');
                    if (this.viewModel.isEnabled() !== true) states.push('disabled');
                    if (this.viewModel.values().length === this.viewModel.maxSelectedItems()) states.push('complete');

                    return states;
                }
            })
        });

        if (!Utilities.isNullOrUndefined(this.viewModel.locator)) {
            this.applyBindings(widget.wrapper, {
                locator: LocatorHelper.buildLocator(this.viewModel.locator, 'outer-wrapper')
            });
            this.applyBindings(widget.wrapper.find('.k-multiselect-wrap'), {
                locator: LocatorHelper.buildLocator(this.viewModel.locator, 'inner-wrapper')
            });
            this.applyBindings(widget.input, {
                locator: LocatorHelper.buildLocator(this.viewModel.locator, 'input')
            });

            KendoDropDownHelper.bindWidgetPopupLocators(widget, this.viewModel.locator);

            let changeNr = 0;
            widget.listView.bind('change', (change) => {

                if (change.added == null || change.added.length === 0) {
                    return;
                }

                // Das Observable values wird auch erst durch change ge�ndert;
                // allerdings NACH diesem Callback, daher warten wir 1ms ab

                let myChangeNr = ++changeNr;
                this.async(() => {
                    if (myChangeNr != changeNr) return;

                    let tags = widget.tagList.children();

                    tags.each((idx: number, element: HTMLElement) => {
                        let $this = $(element);

                        let $childElems = $this.children().not('[data-locator]');
                        if ($childElems.length === 0) return;

                        let $button = $childElems.filter('.k-select');
                        if ($button.length === 1) {
                            this.applyBindings($button, {
                                autoLocator: LocatorHelper.buildLocator(this.viewModel.locator(), 'chosen', idx.toString(), 'remove')
                            });
                        }

                        let $label = $childElems.not('.k-select');
                        if ($label.length === 1) {
                            this.applyBindings($label, {
                                autoLocator: LocatorHelper.buildLocator(this.viewModel.locator(), 'chosen', idx.toString(), 'label')
                            });
                        }
                    });

                    if (!this.jQueryControl.attr('tabindex')) {
                        this.jQueryControl.attr('tabindex', -1);
                    }
                }, 1);
            });

        }

        this.applyBindings(widget.ul.closest('.k-list-container'), {
            controlName: 'multiselectautocomplete-container'
        });

        widget._resizePopup = this.resetDropDownPopupPosition.bind(this);
    }

    private resetDropDownPopupPosition() {
        const labels = this.viewModel.dataSource.data().map((value: string) => {
            return value[this.viewModel.dataTextField];
        });

        KendoDropDownHelper.resetDropDownPopupPosition(labels, this.jQueryControl, this.widget());
    }

    public addLabelReference(label: JQuery): JQuery {
        let multiDropDown = this.getControl();
        let id = this.identifier + '-input';

        multiDropDown.find('input').attr('id', id);
        label.attr('for', id);

        label.click(function () {
            multiDropDown.find('select').data("kendoMultiSelect").open();
        });

        return label;
    }
}

ComponentManager.setControl('MultiSelectAutoComplete', MultiSelectAutoComplete, 'RADPresentation.HTMLClient.DefaultViewExtensions');